import { useCallback, useEffect, useState } from "react";
import { getClientsByOrganizationAsync } from "../api/ClientApi";
import { getFollowUpsByOrganizationAsync } from "../api/FollowUpApi";
import { getAppointmentsByOrganizationAsync } from "../api/AppointmentApi";
import { Client } from "../models/ClientModels";
import { Appointment } from "../models/AppointmentModels";
import { FollowUp, FollowUpStatus } from "../models/FollowUpModels";
import { PagedResult } from "../models/PagedModels";
import { Constants } from "../constants/constants";
import { dateInputToISO } from "../utils/DateTime";

// Messaging loads ONE audience at a time (the lazy requirement): the active
// audience's endpoint is queried with the search term + date range + page.
export type MessagingAudience = "all" | "pending" | "due" | "appointments";

export interface MessagingAudienceRow {
  id: string;
  clientId: string;
  fullName: string;
  phone: string;
  dueAt?: string | null;
  followUpId?: string | null;
}

export interface MessagingAudienceQuery {
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}

const AUDIENCE_PAGE_SIZE = 20;

const toRowFromClient = (client: Client): MessagingAudienceRow => ({
  id: client.id,
  clientId: client.id,
  fullName: client.fullName,
  phone: client.phone ?? "",
  dueAt: null,
  followUpId: null,
});

const toRowFromFollowUp = (followUp: FollowUp): MessagingAudienceRow => ({
  id: followUp.id,
  clientId: followUp.clientId,
  fullName: followUp.clientName ?? followUp.clientId,
  phone: followUp.clientPhone ?? "",
  dueAt: followUp.dueAt,
  followUpId: followUp.id,
});

const toRowFromAppointment = (appointment: Appointment): MessagingAudienceRow => ({
  id: appointment.id,
  clientId: appointment.clientId,
  fullName: appointment.clientName ?? appointment.clientId,
  phone: appointment.clientPhone ?? "",
  dueAt: null,
  followUpId: null,
});

interface UseMessagingAudienceReturn {
  rows: MessagingAudienceRow[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
  loading: boolean;
  error: string | null;
  page: number;
  setPage: (page: number) => void;
  reload: () => Promise<void>;
}

export const useMessagingAudience = (
  organizationId: string | undefined,
  audience: MessagingAudience,
  query: MessagingAudienceQuery,
): UseMessagingAudienceReturn => {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<PagedResult<MessagingAudienceRow> | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!organizationId) {
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    const search = query.search?.trim() || undefined;
    try {
      let result: PagedResult<MessagingAudienceRow> | null = null;

      if (audience === "all") {
        const res = await getClientsByOrganizationAsync(token, organizationId, {
          page,
          pageSize: AUDIENCE_PAGE_SIZE,
          search,
        });
        if (res.success && res.result) {
          result = { ...res.result, items: res.result.items.map(toRowFromClient) };
        } else {
          setError(res.message || "Failed to load clients");
        }
      } else if (audience === "pending") {
        const res = await getFollowUpsByOrganizationAsync(token, organizationId, {
          page,
          pageSize: AUDIENCE_PAGE_SIZE,
          search,
          status: FollowUpStatus.Pending,
          dueFrom: dateInputToISO(query.dateFrom),
          dueTo: dateInputToISO(query.dateTo, true),
          uniquePhone: true,
        });
        if (res.success && res.result) {
          result = { ...res.result, items: res.result.items.map(toRowFromFollowUp) };
        } else {
          setError(res.message || "Failed to load follow-ups");
        }
      } else if (audience === "due") {
        const res = await getFollowUpsByOrganizationAsync(token, organizationId, {
          page,
          pageSize: AUDIENCE_PAGE_SIZE,
          search,
          isDue: true,
          dueFrom: dateInputToISO(query.dateFrom),
          dueTo: dateInputToISO(query.dateTo, true),
          uniquePhone: true,
        });
        if (res.success && res.result) {
          result = { ...res.result, items: res.result.items.map(toRowFromFollowUp) };
        } else {
          setError(res.message || "Failed to load follow-ups");
        }
      } else {
        const res = await getAppointmentsByOrganizationAsync(token, organizationId, {
          page,
          pageSize: AUDIENCE_PAGE_SIZE,
          search,
          from: dateInputToISO(query.dateFrom),
          to: dateInputToISO(query.dateTo, true),
          uniquePhone: true,
        });
        if (res.success && res.result) {
          result = {
            ...res.result,
            items: res.result.items.map(toRowFromAppointment),
          };
        } else {
          setError(res.message || "Failed to load appointments");
        }
      }

      if (result) {
        setData(result);
        setError(null);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load audience");
    } finally {
      setLoading(false);
    }
  }, [organizationId, audience, page, query.search, query.dateFrom, query.dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  const reload = useCallback(async () => {
    await load();
  }, [load]);

  return {
    rows: data?.items ?? [],
    pagination: {
      page: data?.page ?? page,
      pageSize: data?.pageSize ?? AUDIENCE_PAGE_SIZE,
      totalCount: data?.totalCount ?? 0,
      totalPages: data?.totalPages ?? 1,
      hasNext: data?.hasNext ?? false,
      hasPrevious: data?.hasPrevious ?? false,
    },
    loading,
    error,
    page,
    setPage,
    reload,
  };
};